import React, { useContext, useEffect, useState } from 'react'
import { AppContext } from '../../store/context'
import { useHistory } from 'react-router-dom'
import EnrollSideBar from './components/EnrollSideBar'
import { Grid } from '@mui/material'
import { IOperationsStatus, ModalityType, Operations, Status, StatusMessages } from '../../types/FaceVerification'
import { RefVideoStream } from '../VideoStream/BiometricFaceStream'
import OperationContainer from '../../components/Operations/styled/OperationContainer'
import OperationTitle from '../../components/Operations/styled/OperationTitle'
import { addSession, showMessage, showOperationSummary } from '../../store/reducer'
import Overview from '../Overview/Overview'
import FaceOperation from './Modalities/FaceOperation'
import VoiceOperation from './Modalities/VoiceOperation'
import { getInitParamsByManagementConfig, getManagementInfo, useOperationStateEffect } from '../../helpers/operationHelper'
import { FVInitParams } from 'neurotec-megamatcherid-management-client'
import { useSnackbar } from 'notistack'
import { APIsContext } from '../../store/APIs/context'
import { startSession } from '../../helpers/webRTC'
import { mmidInitParamsStorage } from '../../config/const'
import { initialFvParameters, useLocalStorage } from '../../helpers/useLocalStorage'
import { NavigationContext } from '../../components/Navigation/store/context'

const Enroll: React.FC = () => {

    const apisState = useContext(APIsContext).state
    const [options] = useLocalStorage<FVInitParams>(mmidInitParamsStorage, initialFvParameters);
    const { enqueueSnackbar } = useSnackbar();
    const [activeModality, setActiveModality] = useState<ModalityType | null>(null);
    const {state, dispatch} = useContext(AppContext)
    const history = useHistory();
    const [checkState, setCheckState] = useState<IOperationsStatus>(IOperationsStatus.IDLE);
    const childRef = React.useRef<RefVideoStream>(null)
    const [faceOperationUUID, setFaceOperationUUID] = useState("");
    const [voiceOperationUUID, setVoiceOperationUUID] = useState("");
    const navigationDispatch = useContext(NavigationContext).dispatch
    const [liveAdjudication, setLiveAdjudication] = useState(false)

    useOperationStateEffect(
        state,
        apisState,
        dispatch,
        checkState,
        (status: IOperationsStatus) => setCheckState(status),
        activeModality,
        (modality: ModalityType | null) => setActiveModality(modality),
        navigationDispatch,
        liveAdjudication,
        enqueueSnackbar,
        faceOperationUUID,
        childRef.current?.cancel,
    )

    useEffect(() => {
        if (state.activeModalities.faceModality) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.FACE_MODALITY
                } else {
                    return prev
                }
            })
        }
        if (state.activeModalities.voiceModality) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.VOICE_MODALITY
                } else {
                    return prev
                }
            })
        }
    }, [state.activeModalities.faceModality,state.activeModalities.voiceModality, history])

    useEffect(() => {
        if (state.activeModalities.faceModality === false && state.activeModalities.voiceModality === false) {
            history.push("/")
        }
    })

    const enroll = async () => {
        getManagementInfo(apisState.ManagementApi).then(res => {
            if (res === null) {
                enqueueSnackbar("Could not connect to the server", {variant: "error"})
                return;
            }
            setLiveAdjudication(res.liveAdjudication)
            let initParams: FVInitParams = getInitParamsByManagementConfig(res, options);
            initParams.subjectId = state.operationState.subjectId;
            initParams.type = Operations.ENROLL;
            startSession(apisState.JanusApi, Operations.ENROLL, initParams).then(res => {
                setCheckState(IOperationsStatus.ACTIVE)
                dispatch(addSession(res.data, initParams));
            }).catch(err => {
                setCheckState(IOperationsStatus.IDLE)
                console.log(err)
                dispatch(showMessage({ message: "Unable to create session: " + err.message, type: "error" }))
            })
        }).catch(e => {
            enqueueSnackbar(e.message, {variant: "error"})
        })
    }
    
    const restart = () => {
        dispatch(showOperationSummary(""));
        setCheckState(IOperationsStatus.IDLE)
    }
    
    const getName = (): string => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return "Face enrollment"
            case ModalityType.VOICE_MODALITY:
                return "Voice enrollment"
            case ModalityType.OVERVIEW:
                return "Overview"
            default: 
                return "Enrollment"
        }
    }

    const getActiveState = () => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return (
                    <FaceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setFaceOperationUUID}
                        overwriteSuccess={liveAdjudication ? {message: StatusMessages[Status.ADJUDICATION_PENDING], type: "info"} : undefined}
                    />
                )
            case ModalityType.VOICE_MODALITY:
                return (
                    <VoiceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setVoiceOperationUUID}
                    />
                )
            case ModalityType.OVERVIEW:
                return  (
                    <Overview
                        faceOperationUUID={faceOperationUUID}
                        voiceOperationUUID={voiceOperationUUID}
                    />
                )
        }
    }

    return (
        <OperationContainer maxWidth={false}>
            <Grid container style={{paddingLeft: "0px", paddingRight: "0px"}}>
                <OperationTitle>
                    {getName()}
                </OperationTitle>
                    {getActiveState()}
                <EnrollSideBar
                    subjectId={state.operationState.subjectId}
                    activeModality={activeModality}
                    checkState={checkState}
                    start={enroll}
                    restart={restart}
                    cancel={childRef.current?.cancel}
                />
            </Grid>
        </OperationContainer>
    );
}

export default Enroll;
